import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import MapaStands from './components/MapaStands';
import { API_BASE } from './config/api';

const API = API_BASE;   // direccion del backend: config/api.js (fuente unica)
const PP = "'Poppins', sans-serif";
const C = {
  principal: '#270f2b',
  acento: '#94b803',
  card: '#3d1540',
  morado: '#672369',
  rojo: '#ff1744',
  verde: '#00b894',
  textoSuave: 'rgba(255,255,255,0.5)',
  borde: 'rgba(148,184,3,0.2)',
};

const headers = () => {
  const token = localStorage.getItem('beep_token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
};

function comprimirImagen(file) {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      const maxW = 800;
      const ratio = Math.min(maxW / img.width, maxW / img.height, 1);
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.8));
    };
    img.src = URL.createObjectURL(file);
  });
}

const colorTipo = (tipo) => {
  const t = String(tipo || '').toUpperCase();
  if (t.includes('PATROCINADOR')) return C.morado;
  if (t.includes('CENTRO')) return '#2563eb';
  if (t.startsWith('AAA')) return C.verde;
  if (t === 'A') return '#6b7280';
  if (t.includes('PASILLO')) return '#f97316';
  if (t.includes('SERVICIO')) return C.rojo;
  return '#6b7280';
};

const formatoHora = (hora) => {
  if (!hora) return '';
  return String(hora).substring(0, 5);
};

const formatoFechaCorta = (fecha) => {
  if (!fecha) return '';
  const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const d = new Date(fecha);
  if (isNaN(d.getTime())) {
    const partes = String(fecha).split('T')[0].split('-');
    if (partes.length === 3) return `${parseInt(partes[2], 10)} ${meses[parseInt(partes[1], 10) - 1]}`;
    return fecha;
  }
  return `${d.getUTCDate()} ${meses[d.getUTCMonth()]}`;
};

const diffMinutos = (programada, real) => {
  if (!programada || !real) return null;
  const [hp, mp] = String(programada).substring(0, 5).split(':').map(Number);
  const [hr, mr] = String(real).substring(0, 5).split(':').map(Number);
  return (hr * 60 + mr) - (hp * 60 + mp);
};

const estadoColorObj = (estado) => {
  if (estado === 'completado') return { bg: '#f0fdf4', border: '#86efac', text: '#14532d' };
  if (estado === 'en_proceso') return { bg: '#fffbeb', border: '#fde68a', text: '#713f12' };
  return { bg: '#fff0f0', border: '#fca5a5', text: '#7f1d1d' };
};

// ═══════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 20, background: '#fff0f0', margin: 20, borderRadius: 8 }}>
          <h3 style={{ color: 'red' }}>Error en ZonaStands:</h3>
          <pre style={{ fontSize: 12, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
            {this.state.error?.toString()}
            {'\n'}
            {this.state.error?.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function ZonaStands({ usuario, onVolver }) {
  // --- ESTADOS ---
  const [stands, setStands] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [tabModo, setTabModo] = useState('llegada');
  const [standSeleccionado, setStandSeleccionado] = useState(null);
  const [panelAbierto, setPanelAbierto] = useState(false);
  const [fotos, setFotos] = useState([]);
  const [fotoActiva, setFotoActiva] = useState(0);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [mostrarFormFoto, setMostrarFormFoto] = useState(false);
  const [tipoFoto, setTipoFoto] = useState('general');
  const [descFoto, setDescFoto] = useState('');
  const [archivoFoto, setArchivoFoto] = useState(null);
  const [toast, setToast] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [errorCarga, setErrorCarga] = useState('');
  const [ancho, setAncho] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [notasLocal, setNotasLocal] = useState('');
  const [notaRetraso, setNotaRetraso] = useState('');
  const [filtroFecha, setFiltroFecha] = useState('todos');
  const [vistaActiva, setVistaActiva] = useState('mapa');
  const [ordenLista, setOrdenLista] = useState('hora');
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);

  // --- REFS ---
  const leafletMapRef = useRef(null);

  const esMovil = ancho < 768;

  // --- EFFECTS ---
  useEffect(() => {
    const onResize = () => setAncho(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // --- CALLBACKS ---
  const mostrarToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }, []);

  const cargarStands = useCallback(async () => {
    setCargando(true);
    setErrorCarga('');
    try {
      const r = await fetch(`${API}/api/expo/stands`, { headers: headers() });
      if (!r.ok) {
        const txt = await r.text();
        setErrorCarga(`Error ${r.status}: ${txt.slice(0, 200)}`);
        setCargando(false);
        return;
      }
      const data = await r.json();
      setStands(data.stands || []);
    } catch (e) {
      setErrorCarga(`Error de red: ${e.message}`);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargarStands(); }, [cargarStands]);

  const cargarFotos = useCallback(async (standId) => {
    try {
      const r = await fetch(`${API}/api/expo/stands/${standId}/fotos`, { headers: headers() });
      const data = await r.json();
      setFotos(data.fotos || []);
    } catch (e) {
      setFotos([]);
    }
  }, []);

  useEffect(() => {
    if (standSeleccionado) {
      cargarFotos(standSeleccionado.id);
      setMostrarFormFoto(false);
      setTipoFoto('general');
      setDescFoto('');
      setArchivoFoto(null);
      setFotoActiva(0);
      setNotasLocal(standSeleccionado.notas || '');
    }
  }, [standSeleccionado, cargarFotos]);

  const abrirStand = useCallback((stand) => {
    setStandSeleccionado(stand);
    setPanelAbierto(true);
  }, []);

  // --- LÓGICA DE DATOS ---
  const LETRAS_STAND = ['A', 'B', 'C', 'D', 'E'];

  const ordenarStands = (arr) => {
    return [...arr].sort((a, b) => {
      const aEsServicio = (a.tipo_stand || '').toUpperCase().includes('SERVICIO') || (a.tipo_stand || '').toUpperCase().includes('PASILLO');
      const bEsServicio = (b.tipo_stand || '').toUpperCase().includes('SERVICIO') || (b.tipo_stand || '').toUpperCase().includes('PASILLO');
      if (aEsServicio && !bEsServicio) return 1;
      if (!aEsServicio && bEsServicio) return -1;
      const aL = LETRAS_STAND.includes(a.numero_stand);
      const bL = LETRAS_STAND.includes(b.numero_stand);
      if (aL && !bL) return -1;
      if (!aL && bL) return 1;
      if (aL && bL) return LETRAS_STAND.indexOf(a.numero_stand) - LETRAS_STAND.indexOf(b.numero_stand);
      return (parseInt(a.numero_stand) || 999) - (parseInt(b.numero_stand) || 999);
    });
  };

  const FECHAS_EXPO = [
    { label: 'Todos', value: 'todos' },
    { label: '10 Abr', value: '2026-04-10' },
    { label: '11 Abr', value: '2026-04-11' },
    { label: '12 Abr', value: '2026-04-12' },
    { label: '13 Abr', value: '2026-04-13' },
  ];

  const standsFiltrados = useMemo(() => {
    return ordenarStands(
      stands.filter(s => {
        if (busqueda.trim()) {
          const q = busqueda.toLowerCase();
          if (!String(s.numero_stand).toLowerCase().includes(q) &&
              !String(s.proveedor || '').toLowerCase().includes(q) &&
              !String(s.no_proveedor || '').toLowerCase().includes(q)) return false;
        }
        if (filtroFecha !== 'todos') {
          const f = s.fecha_entrada_programada ? String(s.fecha_entrada_programada).split('T')[0] : '';
          if (f !== filtroFecha) return false;
        }
        return true;
      })
    );
  }, [stands, busqueda, filtroFecha]); // eslint-disable-line react-hooks/exhaustive-deps

  const getColorStand = useCallback((stand, tab) => {
    const ahora = new Date();
    const hoy = ahora.toISOString().split('T')[0];
    const fechaStand = stand.fecha_entrada_programada ? String(stand.fecha_entrada_programada).split('T')[0] : '';
    const horaStand = stand.hora_entrada_programada || '';

    if (tab === 'llegada') {
      if (stand.estado_llegada === 'completado') return '#86efac';
      if (stand.estado_llegada === 'en_proceso') return '#fde68a';
      if (fechaStand > hoy) return '#e5e7eb';
      if (fechaStand === hoy) {
        if (horaStand) {
          const [h, m] = horaStand.split(':');
          const horaP = new Date();
          horaP.setHours(parseInt(h), parseInt(m), 0);
          if (ahora > horaP) return '#fca5a5';
          return '#fef08a';
        }
        return '#fef08a';
      }
      return '#f87171';
    }
    if (tab === 'nodos') {
      if (stand.estado_nodos === 'completado') return '#86efac';
      if (stand.estado_nodos === 'en_proceso') return '#fde68a';
      if (stand.estado_llegada !== 'completado') return '#e5e7eb';
      return '#fca5a5';
    }
    if (tab === 'equipos') {
      if ((stand.equipos_computo || 0) === 0) return '#e5e7eb';
      if (stand.estado_equipo === 'completado') return '#86efac';
      if (stand.estado_equipo === 'en_proceso') return '#fde68a';
      if (stand.estado_llegada !== 'completado') return '#e5e7eb';
      return '#fca5a5';
    }
    if (tab === 'electrico') {
      if (stand.estado_electrico === 'completado') return '#86efac';
      if (stand.estado_electrico === 'en_proceso') return '#fde68a';
      if (stand.estado_llegada !== 'completado') return '#e5e7eb';
      return '#fca5a5';
    }
    return '#e5e7eb';
  }, []);

  const getBadgeTiempo = (stand) => {
    const ahora = new Date();
    const hoy = ahora.toISOString().split('T')[0];
    const fechaStand = stand.fecha_entrada_programada ? String(stand.fecha_entrada_programada).split('T')[0] : '';
    const horaStand = stand.hora_entrada_programada || '';
    if (stand.estado_llegada === 'completado') {
      const hr = stand.hora_llegada_real ? new Date(stand.hora_llegada_real).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '';
      return { texto: `✓ ${hr}`, color: '#16a34a', bg: '#dcfce7' };
    }
    if (fechaStand > hoy) {
      const d = new Date(fechaStand + 'T00:00:00');
      return { texto: d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }), color: '#888', bg: 'rgba(255,255,255,0.1)' };
    }
    if (fechaStand === hoy && horaStand) {
      const [h, m] = horaStand.split(':');
      const horaP = new Date();
      horaP.setHours(parseInt(h), parseInt(m), 0);
      const diffMin = Math.round((horaP - ahora) / 60000);
      if (diffMin > 0) return { texto: `${diffMin}m`, color: '#92400e', bg: '#fef9c3' };
      return { texto: `${Math.abs(diffMin)}m tarde`, color: '#dc2626', bg: '#fee2e2' };
    }
    if (fechaStand < hoy && fechaStand) return { texto: 'No llego', color: '#dc2626', bg: '#fee2e2' };
    return { texto: '', color: '#888', bg: 'transparent' };
  };

  const stats = useMemo(() => {
    const ahora = new Date();
    const hoy = ahora.toISOString().split('T')[0];
    const all = standsFiltrados;
    if (tabModo === 'llegada') {
      const llegaron = all.filter(s => s.estado_llegada === 'completado').length;
      const esperadosHoy = all.filter(s => {
        if (s.estado_llegada === 'completado') return false;
        const f = s.fecha_entrada_programada ? String(s.fecha_entrada_programada).split('T')[0] : '';
        if (f !== hoy) return false;
        const [h, m] = (s.hora_entrada_programada || '23:59').split(':');
        const horaP = new Date(); horaP.setHours(parseInt(h), parseInt(m), 0);
        return ahora <= horaP;
      }).length;
      const retrasados = all.filter(s => {
        if (s.estado_llegada === 'completado') return false;
        const f = s.fecha_entrada_programada ? String(s.fecha_entrada_programada).split('T')[0] : '';
        if (f > hoy) return false;
        if (f < hoy) return true;
        const [h, m] = (s.hora_entrada_programada || '00:00').split(':');
        const horaP = new Date(); horaP.setHours(parseInt(h), parseInt(m), 0);
        return ahora > horaP;
      }).length;
      const otroDia = all.filter(s => {
        if (s.estado_llegada === 'completado') return false;
        const f = s.fecha_entrada_programada ? String(s.fecha_entrada_programada).split('T')[0] : '';
        return f > hoy;
      }).length;
      return { llegaron, esperadosHoy, retrasados, otroDia };
    }
    if (tabModo === 'nodos') {
      return {
        listos: all.filter(s => s.estado_nodos === 'completado').length,
        proceso: all.filter(s => s.estado_nodos === 'en_proceso').length,
        pendientes: all.filter(s => s.estado_nodos !== 'completado' && s.estado_nodos !== 'en_proceso' && s.estado_llegada === 'completado').length,
        na: all.filter(s => s.estado_llegada !== 'completado').length,
      };
    }
    if (tabModo === 'electrico') {
      return {
        listos: all.filter(s => s.estado_electrico === 'completado').length,
        proceso: all.filter(s => s.estado_electrico === 'en_proceso').length,
        pendientes: all.filter(s => s.estado_electrico !== 'completado' && s.estado_electrico !== 'en_proceso' && s.estado_llegada === 'completado').length,
        na: all.filter(s => s.estado_llegada !== 'completado').length,
      };
    }
    // equipos
    const conEq = all.filter(s => (s.equipos_computo || 0) > 0);
    return {
      listos: conEq.filter(s => s.estado_equipo === 'completado').length,
      proceso: conEq.filter(s => s.estado_equipo === 'en_proceso').length,
      pendientes: conEq.filter(s => s.estado_equipo !== 'completado' && s.estado_equipo !== 'en_proceso' && s.estado_llegada === 'completado').length,
      na: all.filter(s => (s.equipos_computo || 0) === 0).length,
    };
  }, [standsFiltrados, tabModo]);

  // --- ACCIONES ---
  const actualizarEstado = async (standId, tab, nuevoEstado) => {
    setGuardando(true);
    const body = { usuario_id: usuario?.id };
    if (tab === 'llegada') body.estado_llegada = nuevoEstado;
    else if (tab === 'nodos') body.estado_nodos = nuevoEstado;
    else if (tab === 'equipos') body.estado_equipo = nuevoEstado;
    else if (tab === 'electrico') body.estado_electrico = nuevoEstado;
    try {
      const r = await fetch(`${API}/api/expo/stands/${standId}`, {
        method: 'PUT', headers: headers(), body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error('Error');
      const data = await r.json();
      const upd = data.stand;
      setStands(prev => prev.map(s => s.id === standId ? { ...s, ...upd } : s));
      if (standSeleccionado?.id === standId) setStandSeleccionado(prev => ({ ...prev, ...upd }));
      mostrarToast('Estado actualizado');
    } catch (e) {
      mostrarToast('Error al actualizar');
    } finally {
      setGuardando(false);
    }
  };

  const guardarNotas = async () => {
    if (!standSeleccionado) return;
    setGuardando(true);
    try {
      const r = await fetch(`${API}/api/expo/stands/${standSeleccionado.id}`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ notas: notasLocal, usuario_id: usuario?.id }),
      });
      if (!r.ok) throw new Error('Error');
      const data = await r.json();
      setStands(prev => prev.map(s => s.id === standSeleccionado.id ? { ...s, ...data.stand } : s));
      mostrarToast('Notas guardadas');
    } catch (e) {
      mostrarToast('Error al guardar notas');
    } finally {
      setGuardando(false);
    }
  };

  const subirFoto = async () => {
    if (!archivoFoto || !standSeleccionado) return;
    setSubiendoFoto(true);
    try {
      const base64 = await comprimirImagen(archivoFoto);
      const r = await fetch(`${API}/api/expo/stands/${standSeleccionado.id}/fotos`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ tipo: tipoFoto, foto_url: base64, descripcion: descFoto, usuario_id: usuario?.id }),
      });
      if (!r.ok) throw new Error('Error');
      await cargarFotos(standSeleccionado.id);
      setArchivoFoto(null);
      setDescFoto('');
      setMostrarFormFoto(false);
      mostrarToast('Foto subida');
    } catch (e) {
      mostrarToast('Error al subir foto');
    } finally {
      setSubiendoFoto(false);
    }
  };

  // --- GRUPOS PARA LA LISTA ---
  const grupos = useMemo(() => {
    const ahora = new Date();
    const hoy = ahora.toISOString().split('T')[0];
    const retrasados = [], programados = [], llegaron = [], proximos = [];
    standsFiltrados.forEach(s => {
      const f = s.fecha_entrada_programada ? String(s.fecha_entrada_programada).split('T')[0] : '';
      if (s.estado_llegada === 'completado') { llegaron.push(s); return; }
      if (f > hoy) { proximos.push(s); return; }
      if (f === hoy) {
        const [h, m] = (s.hora_entrada_programada || '23:59').split(':');
        const horaP = new Date(); horaP.setHours(parseInt(h), parseInt(m), 0);
        if (ahora > horaP) retrasados.push(s); else programados.push(s);
      } else {
        retrasados.push(s);
      }
    });
    return [
      { key: 'retrasados', label: 'RETRASADOS', items: retrasados, color: '#fca5a5' },
      { key: 'programados', label: 'ESPERADOS HOY', items: programados, color: '#fde68a' },
      { key: 'llegaron', label: 'YA LLEGARON', items: llegaron, color: '#86efac' },
      { key: 'proximos', label: 'PROXIMOS DIAS', items: proximos, color: 'rgba(255,255,255,0.3)' },
    ].filter(g => g.items.length > 0);
  }, [standsFiltrados]);

  // --- STAND RESALTADO POR BUSQUEDA ---
  const standResaltado = useMemo(() => {
    if (!busqueda) return null;
    return stands.find(s =>
      s.numero_stand?.toLowerCase().includes(busqueda.toLowerCase()) ||
      s.proveedor?.toLowerCase().includes(busqueda.toLowerCase())
    );
  }, [busqueda, stands]);

  // --- LEYENDA ---
  const leyenda = tabModo === 'llegada'
    ? [{ c: '#86efac', l: 'Llego' }, { c: '#fef08a', l: 'Esperado' }, { c: '#fca5a5', l: 'Retrasado' }, { c: '#e5e7eb', l: 'Otro dia' }]
    : [{ c: '#86efac', l: 'Listo' }, { c: '#fde68a', l: 'En proceso' }, { c: '#fca5a5', l: 'Pendiente' }, { c: '#e5e7eb', l: 'N/A' }];

  // --- TABS ---
  const tabs = [
    { id: 'llegada', icon: '🚪', label: 'Llegada' },
    { id: 'nodos', icon: '🔌', label: 'Nodos' },
    { id: 'equipos', icon: '💻', label: 'Equipos' },
    { id: 'electrico', icon: '⚡', label: 'Electrico' },
  ];

  // --- STATS labels ---
  const statsLabels = tabModo === 'llegada'
    ? [
        { val: stats.llegaron, label: 'Llegaron', color: '#86efac' },
        { val: stats.esperadosHoy, label: 'Esperados', color: '#fde68a' },
        { val: stats.retrasados, label: 'Retraso', color: '#fca5a5' },
        { val: stats.otroDia, label: 'Otro dia', color: 'rgba(255,255,255,0.3)' },
      ]
    : [
        { val: stats.listos, label: 'Listos', color: '#86efac' },
        { val: stats.proceso, label: 'Proceso', color: '#fde68a' },
        { val: stats.pendientes, label: 'Pendiente', color: '#fca5a5' },
        { val: stats.na, label: 'N/A', color: 'rgba(255,255,255,0.3)' },
      ];

  // --- VISTA SWITCH ---
  const renderVistaSwitch = () => (
    <div style={{ display: 'flex', background: 'rgba(255,255,255,0.08)', borderRadius: 8, padding: 3, margin: '8px 12px' }}>
      <button onClick={() => setVistaActiva('mapa')} style={{
        flex: 1, padding: '6px 0', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: PP,
        background: vistaActiva === 'mapa' ? C.acento : 'transparent',
        color: vistaActiva === 'mapa' ? C.principal : 'rgba(255,255,255,0.6)',
      }}>🗺️ Mapa</button>
      <button onClick={() => setVistaActiva('lista')} style={{
        flex: 1, padding: '6px 0', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: PP,
        background: vistaActiva === 'lista' ? C.acento : 'transparent',
        color: vistaActiva === 'lista' ? C.principal : 'rgba(255,255,255,0.6)',
      }}>📋 Lista</button>
    </div>
  );

  // --- BADGE HELPER ---
  const badgeEstado = (estado) => {
    if (estado === 'completado') return { bg: '#dcfce7', color: '#14532d', text: '✓ Listo' };
    if (estado === 'en_proceso') return { bg: '#fef9c3', color: '#713f12', text: 'En proceso' };
    return { bg: '#fee2e2', color: '#991b1b', text: 'Pendiente' };
  };

  // --- SORTED STANDS FOR TABLE ---
  const standsOrdenados = useMemo(() => {
    const arr = [...standsFiltrados];
    if (ordenLista === 'nombre') arr.sort((a, b) => (a.proveedor || '').localeCompare(b.proveedor || ''));
    else if (ordenLista === 'estado') arr.sort((a, b) => {
      const orden = { completado: 0, en_proceso: 1, pendiente: 2 };
      return (orden[a.estado_llegada] ?? 2) - (orden[b.estado_llegada] ?? 2);
    });
    // default 'hora' — already sorted by ordenarStands
    return arr;
  }, [standsFiltrados, ordenLista]);

  // --- TABLA DESKTOP ---
  const renderTabla = () => {
    const thStyle = { padding: '8px 6px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#999', letterSpacing: 0.5, borderBottom: '2px solid #eee', fontFamily: PP, whiteSpace: 'nowrap' };
    const tdStyle = { padding: '8px 6px', fontSize: 11, borderBottom: '1px solid #f3f4f6', fontFamily: PP };
    const badgeStyle = (b) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: b.bg, color: b.color, whiteSpace: 'nowrap' });

    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff', borderRadius: 12 }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', gap: 8, padding: '10px 12px', borderBottom: '1px solid #eee', alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar..."
            style={{ flex: 1, minWidth: 120, padding: '6px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 12, fontFamily: PP, outline: 'none' }} />
          {tabModo === 'llegada' && FECHAS_EXPO.map(f => (
            <button key={f.value} onClick={() => setFiltroFecha(f.value)} style={{
              padding: '4px 10px', borderRadius: 99, border: 'none', fontSize: 10, cursor: 'pointer', fontFamily: PP,
              background: filtroFecha === f.value ? C.acento : '#f0f0f0',
              color: filtroFecha === f.value ? C.principal : '#666',
              fontWeight: filtroFecha === f.value ? 700 : 400,
            }}>{f.label}</button>
          ))}
          <select value={ordenLista} onChange={(e) => setOrdenLista(e.target.value)}
            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 11, fontFamily: PP, cursor: 'pointer' }}>
            <option value="hora">Ordenar: Hora</option>
            <option value="nombre">Ordenar: Nombre</option>
            <option value="estado">Ordenar: Estado</option>
          </select>
        </div>
        {/* Table */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
              <tr>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Proveedor</th>
                <th style={thStyle}>Tipo</th>
                <th style={thStyle}>Hora</th>
                <th style={thStyle}>Llegada</th>
                <th style={thStyle}>Nodos</th>
                <th style={thStyle}>Equipos</th>
                <th style={thStyle}>Electrico</th>
                <th style={thStyle}>📷</th>
              </tr>
            </thead>
            <tbody>
              {standsOrdenados.map(s => {
                const esSel = standSeleccionado?.id === s.id;
                const bLlegada = getBadgeTiempo(s);
                const bNodos = badgeEstado(s.estado_nodos || 'pendiente');
                const bEquipo = (s.equipos_computo || 0) === 0 ? { bg: '#f3f4f6', color: '#6b7280', text: 'N/A' } : badgeEstado(s.estado_equipo || 'pendiente');
                const bElec = badgeEstado(s.estado_electrico || 'pendiente');
                return (
                  <tr key={s.id} onClick={() => abrirStand(s)} style={{
                    cursor: 'pointer', background: esSel ? '#f0fdf4' : 'transparent',
                    transition: 'background 0.1s',
                  }} onMouseEnter={(e) => { if (!esSel) e.currentTarget.style.background = '#fafafa'; }}
                     onMouseLeave={(e) => { if (!esSel) e.currentTarget.style.background = 'transparent'; }}>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 6, background: colorTipo(s.tipo_stand), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 10, flexShrink: 0 }}>
                          {s.numero_stand}
                        </div>
                      </div>
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: C.principal, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.proveedor || '—'}</td>
                    <td style={{ ...tdStyle, fontSize: 10, color: '#888' }}>{s.tipo_stand || '—'}</td>
                    <td style={{ ...tdStyle, fontSize: 10 }}>{formatoHora(s.hora_entrada_programada) || '—'}</td>
                    <td style={tdStyle}><span style={{ ...badgeStyle(bLlegada), background: bLlegada.bg, color: bLlegada.color }}>{bLlegada.texto || '—'}</span></td>
                    <td style={tdStyle}><span style={badgeStyle(bNodos)}>{bNodos.text}</span></td>
                    <td style={tdStyle}><span style={badgeStyle(bEquipo)}>{bEquipo.text}</span></td>
                    <td style={tdStyle}><span style={badgeStyle(bElec)}>{bElec.text}</span></td>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#888', fontSize: 10 }}>{s.fotos?.length || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '8px 12px', borderTop: '1px solid #eee', fontSize: 11, color: '#888', fontFamily: PP }}>
          {standsOrdenados.length} stands
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════
  // PANTALLA DE CARGA / ERROR
  // ═══════════════════════════════════════
  if (cargando || errorCarga || stands.length === 0) {
    return (
      <ErrorBoundary>
      <div style={{ fontFamily: PP, padding: 20, background: '#f5f5f5', minHeight: '100vh' }}>
        <button onClick={onVolver} style={{
          background: C.principal, color: '#fff', border: 'none', borderRadius: 10,
          padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          fontFamily: PP, marginBottom: 20,
        }}>← Volver</button>
        <div style={{ textAlign: 'center', paddingTop: 40 }}>
          {cargando ? (
            <div style={{ fontSize: 14, color: '#666' }}>Cargando stands...</div>
          ) : errorCarga ? (
            <div>
              <div style={{ fontSize: 14, color: '#dc2626', marginBottom: 12 }}>Error al cargar stands</div>
              <div style={{ background: '#fee2e2', borderRadius: 8, padding: 12, fontSize: 12, color: '#991b1b', textAlign: 'left', marginBottom: 16, wordBreak: 'break-all' }}>{errorCarga}</div>
              <button onClick={cargarStands} style={{ background: C.principal, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontFamily: PP, fontWeight: 700 }}>Reintentar</button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>No se encontraron stands</div>
              <button onClick={cargarStands} style={{ background: C.principal, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontFamily: PP, fontWeight: 700 }}>Reintentar</button>
            </div>
          )}
        </div>
      </div>
      </ErrorBoundary>
    );
  }

  // ═══════════════════════════════════════
  // RENDER PANEL DERECHO / BOTTOM SHEET
  // ═══════════════════════════════════════
  // Helper: check if stand is delayed
  const esRetrasado = (s) => {
    if (!s || s.estado_llegada === 'completado') return false;
    const ahora = new Date();
    const hoy = ahora.toISOString().split('T')[0];
    const f = s.fecha_entrada_programada ? String(s.fecha_entrada_programada).split('T')[0] : '';
    if (f < hoy) return true;
    if (f === hoy && s.hora_entrada_programada) {
      const [h, m] = s.hora_entrada_programada.split(':');
      const horaP = new Date();
      horaP.setHours(parseInt(h), parseInt(m), 0);
      return ahora > horaP;
    }
    return false;
  };

  const renderPanel = () => {
    if (!standSeleccionado || !panelAbierto) return null;
    const s = standSeleccionado;
    const eqCount = s.equipos_computo || 0;

    // Estado actual segun tab
    const estadoActual =
      tabModo === 'llegada' ? (s.estado_llegada || 'pendiente') :
      tabModo === 'nodos' ? (s.estado_nodos || 'pendiente') :
      tabModo === 'equipos' ? (s.estado_equipo || 'pendiente') :
      (s.estado_electrico || 'pendiente');

    // Botones grandes de estado
    const renderBotonesEstado = () => (
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['pendiente', 'en_proceso', 'completado'].map(est => {
          const config = {
            pendiente: { label: 'Sin iniciar', color: '#e5e7eb', textColor: '#6b7280', icon: '⏸' },
            en_proceso: { label: 'En proceso', color: '#fde68a', textColor: '#713f12', icon: '🔄' },
            completado: { label: 'Completado', color: '#86efac', textColor: '#14532d', icon: '✅' },
          }[est];
          return (
            <button key={est} onClick={() => actualizarEstado(s.id, tabModo, est)} disabled={guardando}
              style={{
                flex: 1, padding: '10px 4px', background: estadoActual === est ? config.color : 'white',
                border: `2px solid ${estadoActual === est ? config.color : '#eee'}`,
                borderRadius: 10, cursor: 'pointer', fontSize: 11,
                fontWeight: estadoActual === est ? 700 : 400,
                color: estadoActual === est ? config.textColor : '#999', fontFamily: PP,
              }}>
              <div style={{ fontSize: 16, marginBottom: 4 }}>{config.icon}</div>
              {config.label}
            </button>
          );
        })}
      </div>
    );

    // Alerta de retraso pulsante
    const renderAlertaRetraso = () => {
      if (!esRetrasado(s) || tabModo !== 'llegada' || s.estado_llegada === 'completado') return null;
      return (
        <div style={{
          background: '#fff0f0', border: '1px solid #fca5a5', borderRadius: 10,
          padding: 12, marginBottom: 12, animation: 'pulso 1s infinite',
        }}>
          <div style={{ fontSize: 12, color: '#7f1d1d', fontWeight: 700, marginBottom: 8, fontFamily: PP }}>
            ⚠️ Proveedor retrasado
          </div>
          <textarea value={notaRetraso} onChange={(e) => setNotaRetraso(e.target.value)}
            placeholder="Agregar nota sobre el retraso..."
            style={{ width: '100%', border: '1px solid #fca5a5', borderRadius: 6, padding: 6, fontSize: 11, resize: 'none', height: 50, fontFamily: PP, boxSizing: 'border-box' }} />
          <button onClick={async () => {
            if (!notaRetraso.trim()) return;
            await actualizarEstado(s.id, 'llegada', s.estado_llegada || 'pendiente');
            const current = s.notas || '';
            const nueva = `[RETRASO] ${notaRetraso.trim()}`;
            setNotasLocal(current ? `${current}\n${nueva}` : nueva);
            await guardarNotas();
            setNotaRetraso('');
            mostrarToast('Nota de retraso guardada');
          }} style={{
            marginTop: 6, width: '100%', background: '#fca5a5', border: 'none', borderRadius: 6,
            padding: 6, fontSize: 11, cursor: 'pointer', color: '#7f1d1d', fontWeight: 700, fontFamily: PP,
          }}>📋 Guardar nota de retraso</button>
        </div>
      );
    };

    // Carrete de fotos reutilizable
    const renderFotos = () => (
      <>
        {fotos.length > 0 ? (
          <>
            <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', background: '#000', marginBottom: 8 }}>
              <img src={fotos[fotoActiva]?.foto_url} alt="" style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }} />
              {fotos.length > 1 && (
                <>
                  <button onClick={() => setFotoActiva(i => Math.max(0, i - 1))} style={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: 99, width: 28, height: 28, cursor: 'pointer', fontSize: 14 }}>‹</button>
                  <button onClick={() => setFotoActiva(i => Math.min(fotos.length - 1, i + 1))} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: 99, width: 28, height: 28, cursor: 'pointer', fontSize: 14 }}>›</button>
                </>
              )}
              <div style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: 99, padding: '2px 8px', fontSize: 10 }}>{fotoActiva + 1}/{fotos.length}</div>
              {fotos[fotoActiva]?.tipo && (
                <div style={{ position: 'absolute', bottom: 6, left: 6, background: 'rgba(39,15,43,0.8)', color: C.acento, borderRadius: 99, padding: '2px 8px', fontSize: 10 }}>{fotos[fotoActiva].tipo}</div>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 6, fontFamily: PP }}>
              {fotos[fotoActiva]?.subido_por_nombre || 'Tecnico'} · {fotos[fotoActiva]?.creado_en ? new Date(fotos[fotoActiva].creado_en).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : ''}
            </div>
            <div style={{ display: 'flex', gap: 4, overflowX: 'auto', marginBottom: 10, paddingBottom: 4 }}>
              {fotos.map((f, i) => (
                <img key={f.id || i} src={f.foto_url} alt="" onClick={() => setFotoActiva(i)} style={{
                  width: 44, height: 44, borderRadius: 6, objectFit: 'cover', flexShrink: 0, cursor: 'pointer',
                  border: i === fotoActiva ? `2px solid ${C.acento}` : '2px solid transparent',
                  opacity: i === fotoActiva ? 1 : 0.7,
                }} />
              ))}
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
            <div style={{ fontSize: 12, color: '#999', fontFamily: PP }}>Sin fotos aun</div>
          </div>
        )}
        {!mostrarFormFoto ? (
          <button onClick={() => setMostrarFormFoto(true)} style={{
            width: '100%', padding: 8, borderRadius: 8, border: '1px dashed #ccc',
            background: '#fafafa', fontFamily: PP, fontWeight: 600, fontSize: 11,
            color: '#666', cursor: 'pointer', marginBottom: 12,
          }}>📷 Agregar foto</button>
        ) : (
          <div style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: 10, marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
              {['llegada', 'nodos', 'equipos', 'incidente', 'general'].map(t => (
                <button key={t} onClick={() => setTipoFoto(t)} style={{
                  padding: '4px 8px', borderRadius: 20, background: tipoFoto === t ? C.acento : '#fff',
                  color: tipoFoto === t ? C.principal : '#666', fontFamily: PP, fontWeight: 600,
                  fontSize: 10, cursor: 'pointer', border: '1px solid #ddd', textTransform: 'capitalize',
                }}>{t}</button>
              ))}
            </div>
            <label style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#fff', border: '2px dashed #ddd', borderRadius: 8,
              padding: 14, cursor: 'pointer', color: '#aaa', fontSize: 12, marginBottom: 8,
            }}>
              {archivoFoto ? `✅ ${archivoFoto.name}` : '📷 Seleccionar foto'}
              <input type="file" accept="image/*" capture="environment"
                onChange={(e) => setArchivoFoto(e.target.files?.[0] || null)} style={{ display: 'none' }} />
            </label>
            <textarea placeholder="Descripcion (opcional)" value={descFoto}
              onChange={(e) => setDescFoto(e.target.value)} rows={2}
              style={{ width: '100%', padding: 6, borderRadius: 6, border: '1px solid #ddd', fontFamily: PP, fontSize: 11, resize: 'none', marginBottom: 8, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => { setMostrarFormFoto(false); setArchivoFoto(null); setDescFoto(''); }}
                style={{ flex: 1, padding: 8, borderRadius: 8, border: '1px solid #ddd', background: '#fff', fontFamily: PP, fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={subirFoto} disabled={!archivoFoto || subiendoFoto}
                style={{ flex: 2, padding: 8, borderRadius: 8, border: 'none', background: (!archivoFoto || subiendoFoto) ? '#ccc' : C.acento, color: C.principal, fontFamily: PP, fontWeight: 700, fontSize: 11, cursor: (!archivoFoto || subiendoFoto) ? 'not-allowed' : 'pointer' }}>
                {subiendoFoto ? 'Subiendo...' : 'Subir foto'}
              </button>
            </div>
          </div>
        )}
      </>
    );

    // Contenido especifico por tab
    const renderContenidoTab = () => {
      if (tabModo === 'llegada') {
        let infoLlegada = null;
        if (s.estado_llegada === 'completado' && s.hora_llegada_real) {
          const diff = diffMinutos(s.hora_entrada_programada, s.hora_llegada_real);
          let texto = `Llego a las ${formatoHora(s.hora_llegada_real)}`;
          let color = C.verde;
          if (diff !== null) {
            if (diff < -5) { texto += ` · Adelantado ${Math.abs(diff)} min`; }
            else if (diff <= 5) { texto += ' · Puntual'; }
            else { texto += ` · Retrasado ${diff} min`; color = '#dc2626'; }
          }
          infoLlegada = <div style={{ fontFamily: PP, fontSize: 11, color, fontWeight: 600, marginBottom: 8 }}>{texto}</div>;
        }
        return (
          <>
            <div style={{ fontSize: 10, color: '#999', marginBottom: 6, letterSpacing: 1, fontFamily: PP }}>LLEGADA</div>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 8, fontFamily: PP }}>
              Programada: {formatoFechaCorta(s.fecha_entrada_programada)} a las {formatoHora(s.hora_entrada_programada) || '—'}
            </div>
            {infoLlegada}
            {renderBotonesEstado()}
            {renderAlertaRetraso()}
          </>
        );
      }

      if (tabModo === 'nodos' || tabModo === 'electrico') {
        const label = tabModo === 'nodos' ? 'NODOS DE RED' : 'ELECTRICO';
        const icon = tabModo === 'nodos' ? '🔌' : '⚡';
        const tecnico = tabModo === 'nodos' ? s.tecnico_nodos : s.tecnico_electrico;
        const hora = tabModo === 'nodos' ? s.hora_nodos : s.hora_electrico;
        return (
          <>
            <div style={{ fontSize: 10, color: '#999', marginBottom: 6, letterSpacing: 1, fontFamily: PP }}>{icon} {label}</div>
            {estadoActual === 'completado' && hora && (
              <div style={{ fontFamily: PP, fontSize: 11, color: C.verde, fontWeight: 600, marginBottom: 8 }}>
                ✓ Completado a las {formatoHora(hora)} {tecnico ? `· Tec. #${tecnico}` : ''}
              </div>
            )}
            {renderBotonesEstado()}
            <div style={{ fontSize: 10, color: '#999', marginBottom: 6, marginTop: 8, letterSpacing: 1, fontFamily: PP }}>FOTOS {fotos.length > 0 && `(${fotos.length})`}</div>
            {renderFotos()}
          </>
        );
      }

      if (tabModo === 'equipos') {
        return (
          <>
            <div style={{ fontSize: 10, color: '#999', marginBottom: 6, letterSpacing: 1, fontFamily: PP }}>💻 EQUIPOS DE COMPUTO</div>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 8, fontFamily: PP }}>
              {eqCount > 0 ? `${eqCount} equipos asignados` : 'Sin equipos asignados'}
            </div>
            {eqCount > 0 && (
              <>
                {estadoActual === 'completado' && s.hora_equipo && (
                  <div style={{ fontFamily: PP, fontSize: 11, color: C.verde, fontWeight: 600, marginBottom: 8 }}>
                    ✓ Instalados a las {formatoHora(s.hora_equipo)} {s.tecnico_equipo ? `· Tec. #${s.tecnico_equipo}` : ''}
                  </div>
                )}
                {renderBotonesEstado()}
              </>
            )}
            <div style={{ fontSize: 10, color: '#999', marginBottom: 6, marginTop: 8, letterSpacing: 1, fontFamily: PP }}>FOTOS {fotos.length > 0 && `(${fotos.length})`}</div>
            {renderFotos()}
          </>
        );
      }

      // montaje — solo fotos
      return (
        <>
          <div style={{ fontSize: 10, color: '#999', marginBottom: 6, letterSpacing: 1, fontFamily: PP }}>📸 FOTOS DEL MONTAJE {fotos.length > 0 && `(${fotos.length})`}</div>
          {renderFotos()}
        </>
      );
    };

    // Grid resumen de estado 2x2
    const estadoItems = [
      { tab: 'llegada', icon: '🚪', label: 'Llegada', estado: s.estado_llegada || 'pendiente' },
      { tab: 'nodos', icon: '🔌', label: 'Nodos', estado: s.estado_nodos || 'pendiente' },
      { tab: 'equipos', icon: '💻', label: `${eqCount} eq`, estado: eqCount === 0 ? 'na' : (s.estado_equipo || 'pendiente') },
      { tab: 'electrico', icon: '⚡', label: 'Electrico', estado: s.estado_electrico || 'pendiente' },
    ];

    const panelContent = (
      <>
        {/* Header oscuro */}
        <div style={{ background: C.principal, padding: '12px 16px', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.acento, fontFamily: PP }}>Stand #{s.numero_stand}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontFamily: PP }}>{s.proveedor || 'Sin proveedor'}</div>
            </div>
            <button onClick={() => setPanelAbierto(false)} style={{
              background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6,
              color: 'white', padding: '4px 8px', cursor: 'pointer', fontSize: 14,
            }}>✕</button>
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 6, fontFamily: PP }}>
            {s.tipo_stand} · {s.medidas || '—'}m
          </div>
        </div>

        {/* Contenido scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
          {/* Grid de estado 2x2 */}
          <div style={{ fontSize: 10, color: '#999', marginBottom: 8, letterSpacing: 1, fontFamily: PP }}>RESUMEN</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 16 }}>
            {estadoItems.map(item => {
              const col = item.estado === 'na'
                ? { bg: '#f3f4f6', border: '#d1d5db', text: '#6b7280' }
                : estadoColorObj(item.estado);
              const isActive = item.tab === tabModo;
              return (
                <div key={item.tab} onClick={() => setTabModo(item.tab)} style={{
                  background: col.bg, border: `${isActive ? 2 : 1}px solid ${isActive ? C.acento : col.border}`,
                  borderRadius: 8, padding: 8, textAlign: 'center', cursor: 'pointer',
                }}>
                  <div style={{ fontSize: 18, marginBottom: 2 }}>{item.icon}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: col.text, fontFamily: PP }}>
                    {item.estado === 'completado' ? '✓ Listo' : item.estado === 'en_proceso' ? 'En proceso' : item.estado === 'na' ? 'N/A' : item.label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Contenido especifico del tab */}
          {renderContenidoTab()}

          {/* Notas (siempre visible) */}
          <div style={{ fontSize: 10, color: '#999', marginBottom: 6, marginTop: 12, letterSpacing: 1, fontFamily: PP }}>NOTAS</div>
          <textarea value={notasLocal} onChange={(e) => setNotasLocal(e.target.value)}
            placeholder="Notas del stand..." rows={3}
            style={{ width: '100%', border: '1px solid #eee', borderRadius: 8, padding: 8, fontSize: 12, resize: 'none', fontFamily: PP, boxSizing: 'border-box', marginBottom: 6 }} />
          <button onClick={guardarNotas} disabled={guardando} style={{
            width: '100%', padding: 8, borderRadius: 8, border: 'none',
            background: guardando ? '#ccc' : C.acento, color: C.principal,
            fontFamily: PP, fontWeight: 700, fontSize: 11, cursor: guardando ? 'not-allowed' : 'pointer',
          }}>{guardando ? 'Guardando...' : 'Guardar notas'}</button>
        </div>
      </>
    );

    // MOVIL: panel inline (renderizado directamente en el layout móvil)
    if (esMovil) {
      return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff' }}>
          {/* Header del stand */}
          <div style={{ background: C.principal, padding: '12px 16px', flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.acento, fontFamily: PP }}>Stand #{s.numero_stand}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontFamily: PP }}>{s.proveedor || 'Sin proveedor'}</div>
              </div>
              <button onClick={() => { setStandSeleccionado(null); setPanelAbierto(false); }} style={{
                background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8,
                color: 'white', padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontFamily: PP,
              }}>✕ Cerrar</button>
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 6, fontFamily: PP }}>
              {s.tipo_stand} · {s.medidas || '—'}m
            </div>
          </div>
          {/* Contenido scrollable */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
            {/* Grid de estado 2x2 */}
            <div style={{ fontSize: 10, color: '#999', marginBottom: 8, letterSpacing: 1, fontFamily: PP }}>RESUMEN</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 16 }}>
              {estadoItems.map(item => {
                const col = item.estado === 'na'
                  ? { bg: '#f3f4f6', border: '#d1d5db', text: '#6b7280' }
                  : estadoColorObj(item.estado);
                const isActive = item.tab === tabModo;
                return (
                  <div key={item.tab} onClick={() => setTabModo(item.tab)} style={{
                    background: col.bg, border: `${isActive ? 2 : 1}px solid ${isActive ? C.acento : col.border}`,
                    borderRadius: 8, padding: 8, textAlign: 'center', cursor: 'pointer',
                  }}>
                    <div style={{ fontSize: 18, marginBottom: 2 }}>{item.icon}</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: col.text, fontFamily: PP }}>
                      {item.estado === 'completado' ? '✓ Listo' : item.estado === 'en_proceso' ? 'En proceso' : item.estado === 'na' ? 'N/A' : item.label}
                    </div>
                  </div>
                );
              })}
            </div>
            {renderContenidoTab()}
            <div style={{ fontSize: 10, color: '#999', marginBottom: 6, marginTop: 12, letterSpacing: 1, fontFamily: PP }}>NOTAS</div>
            <textarea value={notasLocal} onChange={(e) => setNotasLocal(e.target.value)}
              placeholder="Notas del stand..." rows={3}
              style={{ width: '100%', border: '1px solid #eee', borderRadius: 8, padding: 8, fontSize: 12, resize: 'none', fontFamily: PP, boxSizing: 'border-box', marginBottom: 6 }} />
            <button onClick={guardarNotas} disabled={guardando} style={{
              width: '100%', padding: 8, borderRadius: 8, border: 'none',
              background: guardando ? '#ccc' : C.acento, color: C.principal,
              fontFamily: PP, fontWeight: 700, fontSize: 11, cursor: guardando ? 'not-allowed' : 'pointer',
            }}>{guardando ? 'Guardando...' : 'Guardar notas'}</button>
          </div>
        </div>
      );
    }

    // DESKTOP: panel derecho
    return (
      <div style={{
        width: 280, background: '#fff', borderLeft: '1px solid #eee',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        flexShrink: 0, animation: 'slideInRight 0.25s ease',
      }}>
        {panelContent}
      </div>
    );
  };

  // ═══════════════════════════════════════
  // SIDEBAR ITEM
  // ═══════════════════════════════════════
  const renderSidebarItem = (s) => {
    const badge = getBadgeTiempo(s);
    const esSel = standSeleccionado?.id === s.id;
    return (
      <div key={s.id} onClick={() => abrirStand(s)} style={{
        padding: '8px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
        background: esSel ? 'rgba(148,184,3,0.15)' : 'transparent',
        borderLeft: esSel ? `3px solid ${C.acento}` : '3px solid transparent',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 6, background: colorTipo(s.tipo_stand),
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: PP, fontWeight: 700, fontSize: 10, flexShrink: 0,
          }}>#{s.numero_stand}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: PP, fontWeight: 600, fontSize: 11, color: 'rgba(255,255,255,0.9)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{s.proveedor || 'Sin proveedor'}</div>
            <div style={{ fontFamily: PP, fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>
              {formatoHora(s.hora_entrada_programada) || '—'} · {s.tipo_stand || '—'}
            </div>
          </div>
          {badge.texto && (
            <span style={{
              fontSize: 8, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
              background: badge.bg, color: badge.color, whiteSpace: 'nowrap', flexShrink: 0, fontFamily: PP,
            }}>{badge.texto}</span>
          )}
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════
  // LAYOUT MOVIL
  // ═══════════════════════════════════════
  if (esMovil) {
    const filtroActivo = tabModo !== 'llegada' || filtroFecha !== 'todos';
    const badgeFiltroTexto = tabModo !== 'llegada'
      ? tabs.find(t => t.id === tabModo)?.label
      : filtroFecha !== 'todos'
        ? FECHAS_EXPO.find(f => f.value === filtroFecha)?.label
        : null;

    return (
      <ErrorBoundary>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: PP, overflow: 'hidden', background: '#f3f4f4' }}>
        <style>{`
          @keyframes slideUpPanel { from { transform: translateY(100%); } to { transform: translateY(0); } }
          @keyframes pulso { 0%,100% { opacity: 1; } 50% { opacity: 0.7; } }
          @keyframes slideUpSheet { from { transform: translateY(100%); } to { transform: translateY(0); } }
        `}</style>

        {/* Toast */}
        {toast && <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', background: C.principal, color: '#fff', padding: '8px 16px', borderRadius: 20, fontFamily: PP, fontSize: 12, fontWeight: 600, zIndex: 300 }}>{toast}</div>}

        {/* 1. HEADER mínimo 44px */}
        <div style={{ height: 44, background: C.principal, display: 'flex', alignItems: 'center', padding: '0 12px', flexShrink: 0 }}>
          <button onClick={onVolver} style={{ background: 'none', border: 'none', color: C.acento, fontSize: 16, cursor: 'pointer', fontFamily: PP, fontWeight: 700, padding: 0, marginRight: 10 }}>←</button>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.acento, fontFamily: PP }}>Zona de Stands</div>
        </div>

        {/* Si hay stand seleccionado: panel completo */}
        {standSeleccionado ? (
          renderPanel()
        ) : (
          <>
            {/* 2. BUSCADOR */}
            <div style={{ background: C.principal, padding: '0 12px 8px', flexShrink: 0 }}>
              <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar stand o proveedor..."
                style={{ width: '100%', background: 'rgba(255,255,255,0.08)', border: `1px solid ${C.borde}`, borderRadius: 8, padding: '8px 10px', color: 'white', fontSize: 12, fontFamily: PP, outline: 'none', boxSizing: 'border-box' }} />
            </div>

            {/* 3. MAPA 240px (solo en vista mapa) */}
            {vistaActiva === 'mapa' && (
              <div style={{ height: 240, flexShrink: 0, overflow: 'hidden', background: '#f0f0f0', width: '100%' }}>
                <MapaStands stands={standsFiltrados} tabActivo={tabModo} onStandClick={abrirStand}
                  getColorStand={getColorStand} standSeleccionado={standSeleccionado} busqueda={busqueda}
                  standResaltado={standResaltado} />
              </div>
            )}

            {/* 4. LEYENDA mini 20px (solo en vista mapa) */}
            {vistaActiva === 'mapa' && (
              <div style={{ height: 20, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: '#fff', borderBottom: '1px solid #eee' }}>
                {leyenda.map(l => (
                  <div key={l.l} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: l.c }} />
                    <span style={{ fontSize: 8, color: '#666', fontFamily: PP }}>{l.l}</span>
                  </div>
                ))}
              </div>
            )}

            {/* 5. STATS 40px */}
            <div style={{ height: 40, flexShrink: 0, display: 'flex', gap: 4, padding: '0 8px', alignItems: 'center', background: '#fff', borderBottom: '1px solid #eee' }}>
              {statsLabels.map(st => (
                <div key={st.label} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: st.color === 'rgba(255,255,255,0.3)' ? '#888' : st.color, fontFamily: PP, lineHeight: 1 }}>{st.val}</div>
                  <div style={{ fontSize: 7, color: '#999', fontFamily: PP, lineHeight: 1.2 }}>{st.label}</div>
                </div>
              ))}
            </div>

            {/* 6. LISTA SCROLLABLE */}
            <div style={{ flex: 1, overflowY: 'auto', background: '#f5f5f5', padding: 8, paddingBottom: 68 }}>
              {grupos.map(g => (
                <React.Fragment key={g.key}>
                  <div style={{ fontSize: 9, color: '#999', padding: '8px 4px 4px', fontWeight: 700, letterSpacing: 1, fontFamily: PP }}>{g.label} ({g.items.length})</div>
                  {g.items.map(s => {
                    const badge = getBadgeTiempo(s);
                    return (
                      <div key={s.id} onClick={() => abrirStand(s)} style={{
                        background: '#fff', borderRadius: 10, padding: 10, marginBottom: 6,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', cursor: 'pointer',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: colorTipo(s.tipo_stand), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: PP, fontWeight: 700, fontSize: 11, flexShrink: 0 }}>#{s.numero_stand}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: PP, fontWeight: 700, fontSize: 12, color: C.principal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.proveedor || 'Sin proveedor'}</div>
                            <div style={{ fontFamily: PP, fontSize: 10, color: '#666' }}>{formatoHora(s.hora_entrada_programada) || '—'} · {s.equipos_computo || 0} eq</div>
                          </div>
                          {badge.texto && <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: badge.bg, color: badge.color, fontFamily: PP }}>{badge.texto}</span>}
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </>
        )}

        {/* 7. MENÚ INFERIOR CONTEXTUAL 60px */}
        <div style={{
          height: 60, flexShrink: 0, background: C.principal, display: 'flex',
          position: 'sticky', bottom: 0, zIndex: 50,
        }}>
          {/* Filtros */}
          <button onClick={() => setFiltrosAbiertos(true)} style={{
            flex: 1, background: 'none', border: 'none', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 2, cursor: 'pointer', position: 'relative',
          }}>
            <span style={{ fontSize: 18 }}>🔍</span>
            <span style={{ fontSize: 9, fontFamily: PP, fontWeight: 600, color: filtroActivo ? C.acento : 'rgba(255,255,255,0.5)' }}>Filtros</span>
            {badgeFiltroTexto && (
              <span style={{ position: 'absolute', top: 4, right: 8, background: C.acento, color: C.principal, fontSize: 7, fontWeight: 700, padding: '1px 4px', borderRadius: 4, fontFamily: PP }}>{badgeFiltroTexto}</span>
            )}
          </button>
          {/* Vista toggle */}
          <button onClick={() => setVistaActiva(v => v === 'mapa' ? 'lista' : 'mapa')} style={{
            flex: 1, background: 'none', border: 'none', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 2, cursor: 'pointer',
          }}>
            <span style={{ fontSize: 18 }}>{vistaActiva === 'mapa' ? '📋' : '🗺️'}</span>
            <span style={{ fontSize: 9, fontFamily: PP, fontWeight: 600, color: 'rgba(255,255,255,0.5)' }}>{vistaActiva === 'mapa' ? 'Lista' : 'Mapa'}</span>
          </button>
          {/* Reportes */}
          <button onClick={() => mostrarToast('Próximamente')} style={{
            flex: 1, background: 'none', border: 'none', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 2, cursor: 'pointer',
          }}>
            <span style={{ fontSize: 18 }}>📊</span>
            <span style={{ fontSize: 9, fontFamily: PP, fontWeight: 600, color: 'rgba(255,255,255,0.5)' }}>Reportes</span>
          </button>
          {/* Avances / Volver */}
          <button onClick={onVolver} style={{
            flex: 1, background: 'none', border: 'none', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 2, cursor: 'pointer',
          }}>
            <span style={{ fontSize: 18 }}>←</span>
            <span style={{ fontSize: 9, fontFamily: PP, fontWeight: 600, color: 'rgba(255,255,255,0.5)' }}>Avances</span>
          </button>
        </div>

        {/* 8. BOTTOM SHEET DE FILTROS */}
        {filtrosAbiertos && (
          <>
            <div onClick={() => setFiltrosAbiertos(false)} style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200,
            }} />
            <div style={{
              position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff',
              borderRadius: '20px 20px 0 0', zIndex: 201, padding: '0 16px 20px',
              animation: 'slideUpSheet 0.25s ease', maxHeight: 'calc(var(--beep-vh) * 70)', overflowY: 'auto',
            }}>
              {/* Handle */}
              <div style={{ width: 40, height: 4, background: '#ccc', borderRadius: 2, margin: '10px auto 16px' }} />

              {/* TIPO DE TRABAJO */}
              <div style={{ fontSize: 10, fontWeight: 700, color: '#999', letterSpacing: 1, marginBottom: 8, fontFamily: PP }}>TIPO DE TRABAJO</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                {tabs.map(t => (
                  <button key={t.id} onClick={() => setTabModo(t.id)} style={{
                    padding: '8px 14px', borderRadius: 99, border: 'none', fontSize: 12, fontWeight: tabModo === t.id ? 700 : 400,
                    background: tabModo === t.id ? C.acento : '#f0f0f0',
                    color: tabModo === t.id ? C.principal : '#666',
                    cursor: 'pointer', fontFamily: PP,
                  }}>{t.icon} {t.label}</button>
                ))}
              </div>

              {/* DÍA DE MONTAJE */}
              <div style={{ fontSize: 10, fontWeight: 700, color: '#999', letterSpacing: 1, marginBottom: 8, fontFamily: PP }}>DÍA DE MONTAJE</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
                {FECHAS_EXPO.map(f => (
                  <button key={f.value} onClick={() => setFiltroFecha(f.value)} style={{
                    padding: '8px 14px', borderRadius: 99, border: 'none', fontSize: 12, fontWeight: filtroFecha === f.value ? 700 : 400,
                    background: filtroFecha === f.value ? C.acento : '#f0f0f0',
                    color: filtroFecha === f.value ? C.principal : '#666',
                    cursor: 'pointer', fontFamily: PP,
                  }}>{f.label}</button>
                ))}
              </div>

              {/* Aplicar */}
              <button onClick={() => setFiltrosAbiertos(false)} style={{
                width: '100%', padding: 14, borderRadius: 12, border: 'none',
                background: C.acento, color: C.principal, fontSize: 14, fontWeight: 700,
                cursor: 'pointer', fontFamily: PP,
              }}>Aplicar filtros</button>
            </div>
          </>
        )}
      </div>
      </ErrorBoundary>
    );
  }

  // ═══════════════════════════════════════
  // LAYOUT DESKTOP
  // ═══════════════════════════════════════
  return (
    <ErrorBoundary>
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', height: 'calc(var(--beep-vh) * 100)', background: '#f3f4f4', fontFamily: PP }}>
      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes pulso { 0%,100% { opacity: 1; } 50% { opacity: 0.7; } }
        @keyframes slideUpPanel { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `}</style>

      {/* Toast */}
      {toast && <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', background: C.principal, color: '#fff', padding: '8px 16px', borderRadius: 20, fontFamily: PP, fontSize: 12, fontWeight: 600, zIndex: 300 }}>{toast}</div>}

      {/* ══════ SIDEBAR IZQUIERDO ══════ */}
      <div style={{ width: 280, background: C.principal, display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '16px 16px 12px', borderBottom: `1px solid ${C.borde}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={onVolver} style={{ background: 'none', border: 'none', color: C.acento, fontSize: 14, cursor: 'pointer', fontFamily: PP, fontWeight: 600, padding: 0 }}>←</button>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.acento, fontFamily: PP }}>EXPO ALIANZA 2026</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: PP }}>Zona de Stands</div>
            </div>
          </div>
        </div>

        {/* Vista switch */}
        {renderVistaSwitch()}

        {/* Buscador */}
        <div style={{ padding: '10px 12px', borderBottom: `1px solid rgba(148,184,3,0.15)` }}>
          <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar stand o proveedor..."
            style={{ width: '100%', background: 'rgba(255,255,255,0.08)', border: `1px solid ${C.borde}`, borderRadius: 8, padding: '8px 10px', color: 'white', fontSize: 12, fontFamily: PP, outline: 'none', boxSizing: 'border-box' }} />
        </div>

        {/* Filtros */}
        <div style={{ padding: '10px 12px', borderBottom: `1px solid rgba(148,184,3,0.15)` }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 8, letterSpacing: 1, fontFamily: PP }}>FILTRAR POR</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTabModo(t.id)} style={{
                background: tabModo === t.id ? C.acento : 'rgba(255,255,255,0.08)',
                color: tabModo === t.id ? C.principal : 'rgba(255,255,255,0.6)',
                border: 'none', borderRadius: 8, padding: '7px 8px', fontSize: 11,
                fontWeight: tabModo === t.id ? 700 : 400, cursor: 'pointer', fontFamily: PP,
              }}>{t.icon} {t.label}</button>
            ))}
          </div>
        </div>

        {/* Fecha pills */}
        {tabModo === 'llegada' && (
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '8px 12px', borderBottom: '1px solid rgba(148,184,3,0.15)' }}>
            {FECHAS_EXPO.map(f => (
              <button key={f.value} onClick={() => setFiltroFecha(f.value)} style={{
                padding: '5px 12px', borderRadius: 99, border: 'none',
                background: filtroFecha === f.value ? C.acento : 'rgba(255,255,255,0.08)',
                color: filtroFecha === f.value ? C.principal : 'rgba(255,255,255,0.6)',
                fontSize: 11, fontWeight: filtroFecha === f.value ? 700 : 400,
                cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, fontFamily: PP,
              }}>{f.label}</button>
            ))}
          </div>
        )}

        {/* Stats 2x2 */}
        <div style={{ padding: '10px 12px', borderBottom: `1px solid rgba(148,184,3,0.15)` }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {statsLabels.map(st => (
              <div key={st.label} style={{
                background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: '8px 6px',
                textAlign: 'center', border: `1px solid rgba(255,255,255,0.06)`,
              }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: st.color, fontFamily: PP }}>{st.val}</div>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', fontFamily: PP }}>{st.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Lista scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
          {grupos.map(g => (
            <React.Fragment key={g.key}>
              <div style={{
                fontSize: 9, color: g.color, padding: '8px 4px 4px', fontWeight: 700,
                letterSpacing: 1, borderBottom: `1px solid ${g.color}33`, marginBottom: 4, fontFamily: PP,
              }}>{g.label} ({g.items.length})</div>
              {g.items.map(s => renderSidebarItem(s))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ══════ AREA CENTRAL: MAPA O TABLA ══════ */}
      {vistaActiva === 'mapa' ? (
        <div style={{ flex: 1, height: '100%', minHeight: 0, position: 'relative', overflow: 'hidden' }}>
          <MapaStands
            stands={standsFiltrados}
            tabActivo={tabModo}
            onStandClick={abrirStand}
            getColorStand={getColorStand}
            standSeleccionado={standSeleccionado}
            busqueda={busqueda}
            standResaltado={standResaltado}
            onMapReady={(map) => { leafletMapRef.current = map; }}
          />
          {/* Leyenda flotante */}
          <div style={{
            position: 'absolute', top: 12, left: 12, zIndex: 1000,
            display: 'flex', gap: 8, background: 'rgba(39,15,43,0.85)',
            borderRadius: 8, padding: '6px 12px', backdropFilter: 'blur(4px)',
          }}>
            {leyenda.map(l => (
              <div key={l.l} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: l.c }} />
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.8)', fontFamily: PP }}>{l.l}</span>
              </div>
            ))}
          </div>
          {/* Zoom controls */}
          <div style={{
            position: 'absolute', bottom: 20, right: panelAbierto ? 296 : 16,
            zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 6,
            transition: 'right 0.25s ease',
          }}>
            <button onClick={() => leafletMapRef.current?.zoomIn()} style={{
              width: 36, height: 36, borderRadius: 8, background: C.principal, color: C.acento,
              border: 'none', fontSize: 18, cursor: 'pointer', fontWeight: 700,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}>+</button>
            <button onClick={() => {
              const map = leafletMapRef.current;
              if (map) map.fitBounds([[0, 0], [1440, 2280]]);
            }} style={{
              width: 36, height: 36, borderRadius: 8, background: C.principal, color: C.acento,
              border: 'none', fontSize: 14, cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}>⌂</button>
            <button onClick={() => leafletMapRef.current?.zoomOut()} style={{
              width: 36, height: 36, borderRadius: 8, background: C.principal, color: C.acento,
              border: 'none', fontSize: 18, cursor: 'pointer', fontWeight: 700,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}>−</button>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, padding: 12, overflow: 'hidden', display: 'flex' }}>
          {renderTabla()}
        </div>
      )}

      {/* ══════ PANEL DERECHO ══════ */}
      {renderPanel()}
    </div>
    </ErrorBoundary>
  );
}
